import type {VoidFunctionComponent} from "react";
import React from "react";
import s from "./BasicPageDocumentation.module.scss";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {getLoremIpsumText} from "../../../util/string-util";
import {miscImages} from "../../../config/config";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import BasicPage from "../../../components/common/basicPage/BasicPage";
import Button from "../../../components/common/Button";
import {NewWindow} from "../documentationTools/NewWindow";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";
import {NavigateFunction, useNavigate } from "react-router-dom";

export interface IBasicPageDocumentationProps {
}

function getHeaderIconPropertyDocumentation(propertyIndex: number, canShowExample: boolean, setCanShowExample: React.Dispatch<React.SetStateAction<boolean>>): IPropertyWrapper {
  const example1 = <BasicPage
    headerIcon={miscImages.whiteMicrophone}
    content={<></>}
  />;

  return {
    isRequired: false,
    name: "headerIcon",
    element: <Property
      key={propertyIndex}
      name={"headerIcon"}
      description={'If provided, will be rendered in the header section of the page before the headerIconComponent.'}
      isRequired={false}
      type={'string'}
      examples={<>
        <Button
          onClick={() => {
            setCanShowExample(true);
          }}
          text={"Show in new window"}
        />
        <br />
        {
          canShowExample && (
            <NewWindow
              onClose={() => {
                setCanShowExample(false);
              }}
            >
              {example1}
            </NewWindow>
          )
        }
        <br />
        <Example title={"headerIcon is set"} renderChildren={false}>
          <div style={{height: 300, border: "1px solid black, overflow: auto"}}>
            {example1}
          </div>
        </Example>
      </>}
    />
  };
}

function getHeaderIconComponentPropertyDocumentation(propertyIndex: number, canShowExample: boolean, setCanShowExample: React.Dispatch<React.SetStateAction<boolean>>): IPropertyWrapper {
  const example1 = <BasicPage
    headerIconComponent={<>
      {getLoremIpsumText(20)}
      <img src={miscImages.usbImage} alt='' width={200} height={200}/>
    </>}
    content={<></>}
  />;

  return {
    isRequired: false,
    name: "headerIconComponent",
    element: <Property
      key={++propertyIndex}
      name={"headerIconComponent"}
      description={'If provided will be rendered in the header section of the page after the headerIcon.'}
      isRequired={false}
      type={'ReactNode'}
      examples={<>
        <Button
          onClick={() => {
            setCanShowExample(true);
          }}
          text={"Show in new window"}
        />
        <br />
        {
          canShowExample && (
            <NewWindow
              onClose={() => {
                setCanShowExample(false);
              }}
            >
              {example1}
            </NewWindow>
          )
        }
        <br />
        <Example title={"headerIconComponent is set"} renderChildren={false}>
          <NewWindow
            onClose={() => {
              setCanShowExample(false);
            }}
          >
            {example1}
          </NewWindow>
        </Example>
      </>}
    />
  };
}

function getPortraitModeHeaderTextPropertyDocumentation(propertyIndex: number, navigate: NavigateFunction): IPropertyWrapper {
  const example1 = <BasicPage
    portraitModeHeaderText={"My header text"}
    content={<></>}
  />;

  // Note: Can't do a normal example here because windows opened via javascript down have access to the device toolbar to change to portrait orientation.
  // Instead, we'll just link to the onboarding example that uses this property.

  return {
    isRequired: false,
    name: "portraitModeHeaderText",
    element: <Property
      key={++propertyIndex}
      name={"portraitModeHeaderText"}
      description={<>
        Text that will appear in the header section of the page if the device is in portrait orientation.
        <Button
          onClick={() => { navigate('/onboarding/setup-feedback-and-scoring/you-are-all-set/you-are-all-set')}}
          text={"Live example from onboarding"}
        />
      </>}
      isRequired={false}
      type={'string'}
      examples={<>
        <Example title={'headerText = "My header text"'} renderChildren={false}>
          {example1}
        </Example>
      </>}
    />
  };
}

function getContentPropertyDocumentation(propertyIndex: number, canShowExample: boolean, setCanShowExample: React.Dispatch<React.SetStateAction<boolean>>): IPropertyWrapper {
  const example1 = <BasicPage
    content={<>
      {getLoremIpsumText(300)}
    </>}
  />;

  return {
    isRequired: true,
    name: "content",
    element: <Property
      key={++propertyIndex}
      name={"content"}
      description={'Content that will appear in the body section of the page.'}
      isRequired={true}
      type={'string | ReactNode'}
      examples={<>
        <Button
          onClick={() => {
            setCanShowExample(true);
          }}
          text={"Show in new window"}
        />
        <br />
        {
          canShowExample && (
            <NewWindow
              onClose={() => {
                setCanShowExample(false);
              }}
            >
              {example1}
            </NewWindow>
          )
        }
        <br />
        <Example title={'headerText = "My header text"'} renderChildren={false}>
          {example1}
        </Example>
      </>}
    />
  };
}

function getShowBackButtonPropertyDocumentation(propertyIndex: number, canShowExample: boolean, setCanShowExample: React.Dispatch<React.SetStateAction<boolean>>): IPropertyWrapper {
  const example1 = <BasicPage
    showBackButton={false}
    content={<></>}
  />;

  return {
    isRequired: false,
    name: "showBackButton",
    element: <Property
      key={++propertyIndex}
      name={"showBackButton"}
      description={'Determines whether or not to show a back button in the header section.'}
      isRequired={false}
      type={'boolean'}
      defaultValue={'true'}
      examples={<>
        <Button
          onClick={() => {
            setCanShowExample(true);
          }}
          text={"Show in new window"}
        />
        <br />
        {
          canShowExample && (
            <NewWindow
              onClose={() => {
                setCanShowExample(false);
              }}
            >
              {example1}
            </NewWindow>
          )
        }
        <br />
        <Example title={'showBackButton = "false"'} renderChildren={false}>
          {example1}
        </Example>
      </>}
    />
  };
}

function getActionButtonPropertyDocumentation(propertyIndex: number, canShowExample: boolean, setCanShowExample: React.Dispatch<React.SetStateAction<boolean>>): IPropertyWrapper {
  const example1 = <BasicPage
    actionButton={{
      text: "My action button",
      onClick: () => {
        // Code to do something
      },
    }}
    content={<></>}
  />;

  return {
    isRequired: false,
    name: "actionButton",
    element: <Property
      key={++propertyIndex}
      name={"actionButton"}
      description={'A button that will appear in the body of the page at the bottom after the content.'}
      isRequired={false}
      type={'IActionButton'}
      examples={<>
        <Button
          onClick={() => {
            setCanShowExample(true);
          }}
          text={"Show in new window"}
        />
        <br />
        {
          canShowExample && (
            <NewWindow
              onClose={() => {
                setCanShowExample(false);
              }}
            >
              {example1}
            </NewWindow>
          )
        }
        <br />
        <Example title={'showBackButton = "false"'} renderChildren={false}>
          {example1}
        </Example>
      </>}
    />
  };
}

function getUseMosaicBackgroundPropertyDocumentation(propertyIndex: number, canShowExample: boolean, setCanShowExample: React.Dispatch<React.SetStateAction<boolean>>): IPropertyWrapper {
  const example1 = <BasicPage
    useMosaicBackground={true}
    content={<></>}
  />;

  return {
    isRequired: false,
    name: "useMosaicBackground",
    element: <Property
      key={++propertyIndex}
      name={"useMosaicBackground"}
      description={'Displays a mosaic background for the header section.'}
      isRequired={false}
      type={'boolean'}
      defaultValue={'false'}
      examples={<>
        <Button
          onClick={() => {
            setCanShowExample(true);
          }}
          text={"Show in new window"}
        />
        <br />
        {
          canShowExample && (
            <NewWindow
              onClose={() => {
                setCanShowExample(false);
              }}
            >
              {example1}
            </NewWindow>
          )
        }
        <br />
        <Example title={'useMosaicBackground = "true"'} renderChildren={false}>
          {example1}
        </Example>
      </>}
    />
  };
}

export const BasicPageDocumentation: VoidFunctionComponent<IBasicPageDocumentationProps> = ({}) => {
  const [canShowExample1, setCanShowExample1] = React.useState(false);
  const [canShowExample2, setCanShowExample2] = React.useState(false);
  const [canShowExample3, setCanShowExample3] = React.useState(false);
  const [canShowExample4, setCanShowExample4] = React.useState(false);
  const [canShowExample5, setCanShowExample5] = React.useState(false);
  const [canShowExample6, setCanShowExample6] = React.useState(false);

  const navigate = useNavigate();

  let propertyIndex = 0;

  const headerIconPropertyDocumentation = getHeaderIconPropertyDocumentation(++propertyIndex, canShowExample1, setCanShowExample1);
  const headerIconComponentPropertyDocumentation = getHeaderIconComponentPropertyDocumentation(++propertyIndex, canShowExample2, setCanShowExample2);
  const portraitModeHeaderTextPropertyDocumentation = getPortraitModeHeaderTextPropertyDocumentation(++propertyIndex, navigate);
  const contentPropertyDocumentation = getContentPropertyDocumentation(++propertyIndex, canShowExample3, setCanShowExample3);
  const showBackButtonPropertyDocumentation = getShowBackButtonPropertyDocumentation(++propertyIndex, canShowExample4, setCanShowExample4);
  const actionButtonPropertyDocumentation = getActionButtonPropertyDocumentation(++propertyIndex, canShowExample5, setCanShowExample5);
  const useMosaicBackgroundPropertyDocumentation = getUseMosaicBackgroundPropertyDocumentation(++propertyIndex, canShowExample6, setCanShowExample6);

  const properties: IPropertyWrapper[] = [
    headerIconPropertyDocumentation,
    headerIconComponentPropertyDocumentation,
    portraitModeHeaderTextPropertyDocumentation,
    contentPropertyDocumentation,
    showBackButtonPropertyDocumentation,
    actionButtonPropertyDocumentation,
    useMosaicBackgroundPropertyDocumentation,
  ];

  sortProperties(properties);

  return <div className={s.container}>
    <ComponentDescription
      name={"BasicPage"}
      description={<>
        <div>
          <p>A template to simplify making simple pages in our app. Page consists of a header section and a body section.</p>
          <p>Template is designed to work on all screen sizes and orientations.</p>
        </div>
      </>}
    />

    <PropertyListHeader properties={properties} />

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider} />}
      </React.Fragment>
    })}
  </div>;
}


